/**
 * CORE-BN consumer — appointment-confirmation follow-up risk
 *
 * The first real planning consumer of front-of-house attention capacity.
 *
 * Why this consumer, and not another
 * ----------------------------------
 * CORE-BN's Downstream Consumers list opens with "intake pacing and
 * appointment acceptance". The OptiQueue **Needs Confirmation** queue is
 * exactly that: bookings the shop has received but not yet accepted,
 * each of which requires somebody at the front desk to pick up a phone
 * and confirm it. Until that call happens the appointment is not real.
 *
 * More precisely, this is the failure the capability record names in its
 * own words:
 *
 *   "A customer is not called back, and the promise is missed for
 *    reasons the promise model cannot see."
 *
 * That sentence describes this queue. The callbacks are front-of-house
 * communication work; whether they get made depends on whether anyone at
 * the desk has attention left; and today the queue counts what is owed
 * while knowing nothing about whether the shop can pay it. Wiring
 * CORE-BN here closes that loop at the one point where the constitution
 * says the loop is open.
 *
 * It is also the safest available integration. This queue schedules
 * nothing, moves nothing, and assigns nobody. The worst thing an
 * incorrect signal can do here is show an unnecessary sentence.
 *
 * What this consumer may and may not do
 * -------------------------------------
 * MAY: raise a risk level, lower confidence, show an advisory line,
 * recommend a human look at the plan.
 *
 * MAY NOT — and structurally cannot, because it returns no action and no
 * mutation: reject an appointment, refuse intake, cancel work, reassign
 * anyone, move a promise, or alter the board. The assessment is a value;
 * acting on it is a person's job.
 *
 * Degradation
 * -----------
 * When CORE-BN has no estimate, `degradedToBaseline` is true and
 * `risk` is `'unknown'`. Callers MUST then render exactly what they
 * rendered before CORE-BN existed. There is no "probably fine" default:
 * an absent estimate produces an absent opinion.
 *
 * Purity: no I/O, no clock, no React.
 */

import type { FohAttentionPlanningSignal } from '../planningSignal';

/**
 * How much concern the shop should feel about clearing its confirmation
 * backlog in this window.
 *
 *   - `'none'`     — nothing outstanding, or ample attention for it.
 *   - `'low'`      — the desk is busy; the backlog should still clear.
 *   - `'elevated'` — the backlog is competing for attention the desk
 *                    does not comfortably have.
 *   - `'high'`     — on the current estimate these callbacks will not
 *                    all get made in this window.
 *   - `'unknown'`  — no front-of-house estimate. Behave as before.
 */
export type ConfirmationFollowUpRisk =
  | 'none'
  | 'low'
  | 'elevated'
  | 'high'
  | 'unknown';

const RISK_ORDER: ReadonlyArray<ConfirmationFollowUpRisk> = Object.freeze([
  'none',
  'low',
  'elevated',
  'high',
]);

/**
 * Backlog depth at which the queue escalates one risk band beyond what
 * front-of-house saturation alone would imply.
 *
 * Saturation describes the desk; this describes the pile on it. A
 * comfortable desk with eleven outstanding callbacks is still a shop
 * about to disappoint someone, and a saturated desk with one is not.
 * Four is the point at which the backlog stops being absorbable between
 * other tasks.
 */
export const FOLLOW_UP_BACKLOG_ESCALATION_THRESHOLD = 4;

export interface ConfirmationFollowUpAssessment {
  risk: ConfirmationFollowUpRisk;

  /** CORE-BN is advisory in Core. Always. */
  readonly advisory: true;
  /** This assessment never blocks, refuses or cancels an appointment. */
  readonly blocksIntake: false;
  /** The underlying attention figure is an estimate, and must read as one. */
  readonly isEstimate: true;

  /**
   * True when there is no front-of-house estimate. Callers must render
   * their pre-CORE-BN behaviour unchanged.
   */
  degradedToBaseline: boolean;

  /** How many bookings are awaiting a confirmation call. */
  pendingCount: number;
  /**
   * Estimated front-of-house attention those calls will consume, using
   * the shop's own configured cost per customer contact. `null` when
   * there is no estimate.
   */
  estimatedAttentionMinutes: number | null;
  /** Attention-minutes left in the window. `null` when there is no estimate. */
  headroomAttentionMinutes: number | null;

  /**
   * One shop-scoped sentence, or `null` when there is nothing to say.
   * Never names or characterises a person.
   */
  message: string | null;
}

export interface AssessConfirmationFollowUpRiskArgs {
  signal: FohAttentionPlanningSignal;
  /** Number of bookings still awaiting a confirmation call. */
  pendingCount: number;
}

function safeCount(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return 0;
  return Math.floor(v);
}

function escalate(risk: ConfirmationFollowUpRisk): ConfirmationFollowUpRisk {
  const i = RISK_ORDER.indexOf(risk);
  if (i < 0) return risk;
  return RISK_ORDER[Math.min(i + 1, RISK_ORDER.length - 1)];
}

/**
 * Assess whether the shop's front of house can be expected to clear its
 * appointment-confirmation backlog in the estimated window.
 *
 * Deterministic and pure: the same signal and the same backlog always
 * produce the same assessment.
 */
export function assessConfirmationFollowUpRisk(
  args: AssessConfirmationFollowUpRiskArgs,
): ConfirmationFollowUpAssessment {
  const pendingCount = safeCount(args?.pendingCount);
  const signal = args?.signal;

  const base = {
    advisory: true as const,
    blocksIntake: false as const,
    isEstimate: true as const,
    pendingCount,
  };

  // No estimate — say nothing, and tell the caller to behave as before.
  if (!signal || signal.degradeToBaseline || signal.state === 'unknown') {
    return Object.freeze({
      ...base,
      risk: 'unknown' as ConfirmationFollowUpRisk,
      degradedToBaseline: true,
      estimatedAttentionMinutes: null,
      headroomAttentionMinutes: null,
      message: null,
    });
  }

  const perCall = signal.claimWeightMinutes?.customer_communication ?? 0;
  const estimatedAttentionMinutes = round2(pendingCount * perCall);
  const headroomAttentionMinutes = signal.headroomAttentionMinutes;

  // Nothing owed cannot be missed, however saturated the desk is.
  if (pendingCount === 0) {
    return Object.freeze({
      ...base,
      risk: 'none' as ConfirmationFollowUpRisk,
      degradedToBaseline: false,
      estimatedAttentionMinutes: 0,
      headroomAttentionMinutes,
      message: null,
    });
  }

  let risk: ConfirmationFollowUpRisk = promiseRiskToFollowUpRisk(signal);

  // At most ONE escalation beyond what the desk's own saturation implies.
  //
  // The two conditions below overlap — a deep backlog is usually also a
  // backlog that does not fit — and applying both would compound a single
  // underlying fact into two bands, turning an ordinary busy morning into
  // a red flag. The arithmetic check is preferred because it is the
  // stronger statement: "these calls do not fit in the attention that
  // remains" is a claim about this window, not a rule of thumb about pile
  // size.
  const doesNotFit =
    typeof headroomAttentionMinutes === 'number' &&
    estimatedAttentionMinutes > headroomAttentionMinutes;

  if (doesNotFit) {
    risk = escalate(risk);
  } else if (pendingCount >= FOLLOW_UP_BACKLOG_ESCALATION_THRESHOLD) {
    risk = escalate(risk);
  }

  return Object.freeze({
    ...base,
    risk,
    degradedToBaseline: false,
    estimatedAttentionMinutes,
    headroomAttentionMinutes,
    message: buildMessage(risk, pendingCount, estimatedAttentionMinutes, headroomAttentionMinutes),
  });
}

function promiseRiskToFollowUpRisk(
  signal: FohAttentionPlanningSignal,
): ConfirmationFollowUpRisk {
  switch (signal.state) {
    case 'comfortable':
      return 'none';
    case 'loaded':
      return 'low';
    case 'saturated':
      return 'elevated';
    case 'over_capacity':
      return 'high';
    default:
      return 'unknown';
  }
}

/**
 * The advisory sentence.
 *
 * Subject is always the shop's front of house, never a person, and never
 * "you are behind". `'none'` and `'low'` deliberately return `null`: an
 * operator queue that comments on itself every time it renders trains
 * people to ignore it, and the constitution wants this signal read when
 * it appears.
 */
function buildMessage(
  risk: ConfirmationFollowUpRisk,
  pendingCount: number,
  estimatedMinutes: number,
  headroomMinutes: number | null,
): string | null {
  if (risk !== 'elevated' && risk !== 'high') return null;

  const calls = pendingCount === 1 ? '1 confirmation call' : `${pendingCount} confirmation calls`;
  const cost = `about ${Math.round(estimatedMinutes)} min of front-desk attention`;
  const spare =
    typeof headroomMinutes === 'number'
      ? ` Front of house has roughly ${Math.round(headroomMinutes)} min spare in this window.`
      : '';

  if (risk === 'high') {
    return `Front of house is unlikely to clear these callbacks in this window — ${calls}, ${cost}.${spare} Worth deciding who picks these up, or accepting the delay knowingly.`;
  }
  return `Front-of-house load is competing with these callbacks — ${calls}, ${cost}.${spare}`;
}

function round2(v: number): number {
  return Math.round(v * 100) / 100;
}
